import { useEffect, useState } from "react";
import type { CSSProperties } from "react";
import { Engraved, Icon, Script } from "../components/ui";
import type { ScreenId } from "../components/ui";
import { FloralSpray } from "../components/Florals";
import type { FloralDensity } from "../components/Florals";
import { MOMENTS, washFor } from "../lib/content";
import { useKeep } from "../lib/keep";
import { api } from "../lib/api";

export type MomentsLayout = "rows" | "editorial" | "petal";

interface MomentCard {
  id: string;
  name: string;
  desc: string;
  count: number;
  coverUrl?: string;
  seed: number;
}

interface MomentsScreenProps {
  go: (s: ScreenId) => void;
  openMoment: (id: string) => void;
  layout: MomentsLayout;
  density: FloralDensity;
}

const mockCards: MomentCard[] = MOMENTS.map((m, i) => ({
  id: m.id,
  name: m.name,
  desc: m.desc,
  count: m.count,
  seed: m.count + i,
}));

function coverStyle(card: MomentCard): CSSProperties {
  if (card.coverUrl) {
    return { backgroundImage: `url(${card.coverUrl})`, backgroundSize: "cover", backgroundPosition: "center" };
  }
  return washFor(card.seed);
}

export function MomentsScreen({ go, openMoment, layout, density }: MomentsScreenProps) {
  const { keepId, live } = useKeep();
  const [cards, setCards] = useState<MomentCard[]>(mockCards);

  useEffect(() => {
    if (!live || !keepId) return;
    api
      .moments(keepId)
      .then((r) =>
        setCards(
          r.moments.map((m, i) => ({
            id: m.key,
            name: m.label,
            desc: m.desc,
            count: m.photoCount,
            coverUrl: m.coverThumbUrl ?? undefined,
            seed: m.photoCount + i,
          })),
        ),
      )
      .catch((err) => console.warn("moments fetch failed:", err));
  }, [live, keepId]);

  return (
    <div className="screen moments">
      <div className="moments-top">
        <FloralSpray variant="cornerTR" density={density} width={150} height={150} seed={5} className="mom-corner" />
        <Engraved size={11.5} style={{ letterSpacing: "0.34em" }}>
          The Album
        </Engraved>
        <Script size={46} className="moments-title">
          Our moments
        </Script>
        <p className="moments-sub">
          Every photo, sorted into the day as it happened. Nothing to tag — it organizes itself.
        </p>
        <button className="search-pill" onClick={() => go("search")}>
          <Icon name="search" size={17} stroke={1.6} />
          <span>Show me the first dance…</span>
        </button>
      </div>

      {layout === "rows" && (
        <div className="mom-rows">
          {cards.map((m) => (
            <button key={m.id} className="mom-row" onClick={() => openMoment(m.id)}>
              <div className="mom-row-cover" style={coverStyle(m)}>
                <div className="ptile-grain" />
                <div className="ptile-vignette" />
              </div>
              <div className="mom-row-text">
                <Engraved size={12.5} className="mom-row-name">
                  {m.name}
                </Engraved>
                <span className="mom-row-desc">{m.desc}</span>
                <span className="mom-row-count">{m.count} photos</span>
              </div>
              <Icon name="chevron" size={18} className="mom-row-chev" />
            </button>
          ))}
        </div>
      )}

      {layout === "editorial" && (
        <div className="mom-edit">
          {cards.map((m, i) => {
            const big = i % 5 === 0;
            return (
              <button key={m.id} className={"mom-edit-card" + (big ? " big" : "")} onClick={() => openMoment(m.id)}>
                <div className="mom-edit-cover" style={coverStyle(m)}>
                  <div className="ptile-grain" />
                  <div className="ptile-vignette" />
                  <span className="mom-edit-count">{m.count}</span>
                </div>
                <Engraved size={big ? 13 : 11} className="mom-edit-name">
                  {m.name}
                </Engraved>
              </button>
            );
          })}
        </div>
      )}

      {layout === "petal" && (
        <div className="mom-petal">
          {cards.map((m) => (
            <button key={m.id} className="mom-petal-card" onClick={() => openMoment(m.id)}>
              <div className="mom-petal-orb" style={coverStyle(m)}>
                <div className="ptile-grain" />
                <span className="mom-petal-count">{m.count}</span>
              </div>
              <Engraved size={10.5} className="mom-petal-name">
                {m.name}
              </Engraved>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
